import React, { useCallback } from 'react'
import useOffer from 'hooks/Offers/useOffer'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import LesaHotelPurchaseItem from './LesaHotelPurchaseItem'
import { ITEM_STATUS_AWAITING_DATES } from 'constants/cart'
import { canCancelOrderItem, canChangeHotelDates, canChangeHotelRooms, getHotelItems } from 'supportAssistant/utils/orderUtils'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'

interface Props {
  order: App.Order;
  isPastOrder: boolean;
  hideSelect?: boolean;
}

export default function LesaHotelPurchases({ order, isPastOrder, hideSelect }: Props) {
  const dispatch = useAppDispatch()

  const [offer]: Array<any> = useOffer<App.Offer>(order.items[0]?.offerId)

  const items = getHotelItems(order)
  const item = items[0]

  const orderOffer = item?.offer
  const isTour = orderOffer?.parentType === OFFER_TYPE_TOUR

  const hasExperiences = !!order.experienceItems.length
  const hasAddons = !!order.addonItems.length
  const hasInsurance = !!order.insuranceItems.length
  const hasFlight = !!order.hasFlight

  const insuranceProductName = useAppSelector(state => state.geo.insuranceProductName)

  const canBeCancelled = canCancelOrderItem(order, item)
  const canChangeDates = canChangeHotelDates(order, offer)
  const canChangeRooms = canChangeHotelRooms(order, offer, items)
  const awaitingDates = item.status === ITEM_STATUS_AWAITING_DATES

  const handleOrderSelect = useCallback(() => {
    if (items.length === 1) {
      dispatch(addChatMessage('', {
        action: 'selectOrder',
        payload: {
          awaitingDates,
          bookingNumber: item.bookingNumber,
          canBeCancelled,
          canChangeDates,
          canChangeRooms,
          checkInDate: item.reservation?.startDate,
          hasFlight,
          id_orders: order.id,
          itemId: item.id,
          orderType: 'hotel',
        },
      }))

      return
    }

    dispatch(addChatMessage('', {
      action: 'selectOrder',
      payload: {
        awaitingDates,
        hasFlight,
        id_orders: order.id,
        orderType: 'hotel',
      },
    }))
  }, [items.length, dispatch, awaitingDates, order.id, item.bookingNumber, item.reservation?.startDate, item.id, canBeCancelled, canChangeDates, canChangeRooms, hasFlight])

  return (
    <>
      {!offer && <LesaPurchaseSummarySkeleton />}
      {offer && <LesaPurchaseSummaryContainer>
        <LesaPurchaseSummaryHeader>
          <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineBedIcon size="XS"/>}>
            {isTour ? 'Tour' : 'Hotel'}
            {hasFlight && ' + Flights'}
            {(hasExperiences || hasAddons) && ' + Experiences'}
            {hasInsurance && ` + Travel ${insuranceProductName} - ${order.insuranceItems[0]?.productName}`}
          </BodyTextBlock>
          {!isPastOrder && <StatusLabel variant="confirmation">Upcoming</StatusLabel>}
          {isPastOrder && <StatusLabel variant="pending">Past</StatusLabel>}
        </LesaPurchaseSummaryHeader>
        <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
          <Group direction="vertical" gap={4}>
            {orderOffer?.location && <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >{orderOffer.location}</CaptionTextBlock>}
            <LesaPurchaseSummaryTitle orderId={order.id}>
              {orderOffer?.name}
            </LesaPurchaseSummaryTitle>{' '}
            {items && <LesaHotelPurchaseItem order={order} isTour={isTour}/>}
          </Group>
          {!hideSelect &&
            <TextButton onClick={handleOrderSelect} fit="mobile-full-width" size="small" kind="secondary">
              Select
            </TextButton>
          }
        </Group>
      </LesaPurchaseSummaryContainer>}
    </>

  )
}
